/** Full-page lifecycle view (/lifecycle/:kind/:id) for an order or device. */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import LifecycleTimeline, { StatusChip } from "@/components/app/LifecycleTimeline";

export default function LifecyclePage() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const r = data?.record;
  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-5" data-testid="lifecycle-page">
      <button onClick={() => navigate(-1)} className="text-[12px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="lifecycle-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-sky-600">{kind === "order" ? "Order" : "Device"} lifecycle</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="lifecycle-title">{r ? `${r.code || ""} ${r.title && r.title !== r.code ? `· ${r.title}` : ""}` : "…"}</h1>
          <div className="text-[12px] text-slate-500 mt-0.5 flex flex-wrap gap-2 items-center">
            {r?.customer && <span>{r.customer}</span>}{r?.serial && <span className="font-mono">S/N {r.serial}</span>}{r?.status && <span className="font-mono rounded bg-slate-100 px-1.5">{r.status}</span>}
            {data?.current_stage && <StatusChip status={data.stages.find((s) => s.key === data.current_stage)?.status} testId="lifecycle-current-status" />}
          </div>
        </div>
        {r?.route && <button onClick={() => navigate(r.route)} data-testid="lifecycle-open-record" className="ml-auto h-8 px-3 rounded-md border border-slate-200 bg-white text-[12px] inline-flex items-center gap-1.5 hover:bg-slate-50"><ExternalLink className="h-3.5 w-3.5" /> Open record</button>}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
        <LifecycleTimeline kind={kind} id={id} onLoaded={setData} />
      </div>
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="lifecycle-stage-cards">
          {data.stages.filter((s) => s.status !== "not_started").map((s) => (
            <div key={s.key} className="rounded-lg border border-slate-200 bg-white px-3.5 py-3">
              <div className="flex items-center gap-2"><span className="text-[13px] font-semibold text-slate-900">{s.label}</span><StatusChip status={s.status} /></div>
              <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                <div>Owner: <span className="text-slate-700">{s.owner || "—"}</span> · {s.at ? String(s.at).slice(0, 16).replace("T", " ") : "—"}</div>
                {s.record && <div className="font-mono truncate">{s.record}</div>}
                {s.pending_reason && <div className="text-amber-700">Pending: {s.pending_reason}</div>}
                {s.sla_state && s.sla_state !== "ok" && <div className={s.sla_state === "breached" ? "text-rose-700" : "text-amber-700"}>SLA {s.sla_state.replace("_", " ")}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
